/**
 * DocuSignQuotaWidget — dashboard card showing current month's envelope
 * usage vs plan cap. Powered by GET /api/docusign/quota.
 *
 * Only surfaces when configured is true and the user is admin (backend
 * returns 403 for others — we degrade gracefully to hidden).
 *
 * Colour ramp: emerald → amber → rose as usage climbs. Once past 90%
 * we show a hard warning strip.
 */
import { useEffect, useState } from "react";
import { FileSignature, AlertTriangle, TrendingUp, Flame, CalendarClock } from "lucide-react";
import { api } from "@/lib/api";

function ringColor(pct) {
  if (pct == null) return "#94A3B8";
  if (pct >= 90) return "#EF4444";     // rose
  if (pct >= 70) return "#F59E0B";     // amber
  return "#10B981";                    // emerald
}

export default function DocuSignQuotaWidget() {
  const [quota, setQuota] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/docusign/quota")
      .then((r) => { if (alive) setQuota(r.data); })
      .catch((e) => { if (alive) setError(e?.response?.status || "err"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  // Hidden for non-admins (backend 403s) or when not configured
  if (error === 403 || (!loading && !quota) || quota?.mock) return null;
  if (loading) {
    return (
      <div
        data-testid="docusign-quota-widget-loading"
        className="rounded-xl border border-slate-200 bg-white p-4"
      >
        <div className="h-3 w-24 bg-slate-100 rounded animate-pulse" />
        <div className="mt-3 h-8 w-32 bg-slate-100 rounded animate-pulse" />
      </div>
    );
  }

  const used = quota.used ?? 0;
  const limit = quota.limit ?? 40;
  const pct = quota.percent ?? Math.min(100, Math.round((used / Math.max(1, limit)) * 100));
  const color = ringColor(pct);
  const over = pct >= 100;
  const near = pct >= 90 && !over;

  const burn = quota.burn_rate || null;
  const projPct = burn?.projected_pct_eom ?? null;
  const projOver = projPct != null && projPct > 100;

  return (
    <div
      data-testid="docusign-quota-widget"
      className="rounded-xl border border-slate-200 bg-white p-4 md:p-5"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-slate-500">
            <FileSignature className="w-3 h-3" />
            DocuSign quota
          </div>
          <div className="mt-1 flex items-baseline gap-1.5">
            <span className="text-3xl font-black tabular-nums text-slate-900" data-testid="docusign-quota-used">{used}</span>
            <span className="text-sm text-slate-500 tabular-nums">/ {limit} envelopes</span>
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            This month · resets 1st
          </div>
        </div>
        <div
          className="flex flex-col items-end shrink-0"
          data-testid="docusign-quota-pct"
        >
          <div
            className="text-lg font-black tabular-nums"
            style={{ color }}
          >
            {pct}%
          </div>
          <div className="text-[9px] uppercase tracking-widest text-slate-400 font-bold">
            Used
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mt-3 h-2 w-full rounded-full bg-slate-100 overflow-hidden relative">
        <div
          className="h-full transition-all duration-500 ease-out"
          style={{
            width: `${Math.min(100, pct)}%`,
            background: color,
          }}
          data-testid="docusign-quota-bar"
        />
        {/* Projected end-of-cycle tick — a hollow marker showing where we
            LAND at the current pace, if it's beyond current usage. */}
        {burn && projPct != null && projPct > pct && projPct <= 100 && (
          <div
            className="absolute top-0 bottom-0 w-[2px] bg-slate-400"
            style={{ left: `${Math.min(100, projPct)}%` }}
            data-testid="docusign-quota-projection-tick"
            title={`Projected end-of-month: ${burn.projected_used_eom} envelopes (${projPct}%)`}
          />
        )}
      </div>

      {/* Burn-rate strip */}
      {burn && (
        <div
          className="mt-3 grid grid-cols-3 gap-2 text-[11px]"
          data-testid="docusign-quota-burn-rate"
        >
          <div className="rounded-md bg-slate-50 border border-slate-200 px-2 py-1.5">
            <div className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest text-slate-500 font-bold">
              <Flame className="w-2.5 h-2.5" /> Pace
            </div>
            <div className="text-sm font-black text-slate-900 tabular-nums">
              {burn.daily_avg}<span className="text-[10px] font-bold text-slate-400 ml-0.5">/day</span>
            </div>
          </div>
          <div className="rounded-md bg-slate-50 border border-slate-200 px-2 py-1.5">
            <div className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest text-slate-500 font-bold">
              <TrendingUp className="w-2.5 h-2.5" /> Projected
            </div>
            <div
              className="text-sm font-black tabular-nums"
              style={{ color: projOver ? "#EF4444" : projPct >= 90 ? "#F59E0B" : "#0F172A" }}
              data-testid="docusign-quota-projected"
            >
              {burn.projected_used_eom}<span className="text-[10px] font-bold text-slate-400 ml-0.5">/mo</span>
            </div>
          </div>
          <div className="rounded-md bg-slate-50 border border-slate-200 px-2 py-1.5">
            <div className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest text-slate-500 font-bold">
              <CalendarClock className="w-2.5 h-2.5" /> Cap in
            </div>
            <div className="text-sm font-black text-slate-900 tabular-nums" data-testid="docusign-quota-days-until-cap">
              {burn.days_until_cap == null
                ? "—"
                : burn.days_until_cap > burn.days_remaining_in_cycle
                  ? "Safe"
                  : `${burn.days_until_cap}d`}
            </div>
          </div>
        </div>
      )}

      {/* Warnings */}
      {over && (
        <div className="mt-3 flex items-start gap-2 text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-2.5 py-2">
          <AlertTriangle size={12} className="mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold">Over your monthly cap</div>
            <div className="text-rose-700/80">
              Upgrade your DocuSign plan or wait until next cycle. Users on the
              free tier can still send via their own connected accounts.
            </div>
          </div>
        </div>
      )}
      {near && (
        <div className="mt-3 flex items-start gap-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-2">
          <TrendingUp size={12} className="mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold">Approaching the cap</div>
            <div className="text-amber-700/80">
              Only {Math.max(0, limit - used)} envelopes remain this cycle.
            </div>
          </div>
        </div>
      )}
      {!over && !near && projOver && (
        <div className="mt-3 flex items-start gap-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-2" data-testid="docusign-quota-projection-warning">
          <AlertTriangle size={12} className="mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold">On pace to blow past your cap</div>
            <div className="text-amber-800/80">
              At {burn.daily_avg}/day, you'll hit {burn.projected_used_eom} by month-end
              ({projPct}% of a {limit}-envelope plan). Consider upgrading before day {burn.days_until_cap ?? "?"}.
            </div>
          </div>
        </div>
      )}
      {quota.error && (
        <div className="mt-2 text-[11px] text-slate-500">
          Live count unavailable — showing last known.
        </div>
      )}
    </div>
  );
}
